import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  CircularProgress,
  Grid,
  Tabs,
  Button,
  Tab,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../../AuthProvider";
import { useNavigate } from "react-router-dom";

export default function LeisurePeriod() {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const { pId } = useAuth();

  const [mode, setMode] = useState(() => localStorage.getItem("lessonAssignMode") || "manual");
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const [leaveTeachers, setLeaveTeachers] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);

   const navigate = useNavigate();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedTeacherId(null);
    if (newValue === 0) fetchTodayLeaveTeachers();
    if (newValue === 1) fetchTomorrowLeaveTeachers();
  };

  useEffect(() => {
    if (mode === "manual") {
      fetchTodayLeaveTeachers();
    }
  }, [mode]);

  const fetchTodayLeaveTeachers = async () => {
    if (!pId) return;
    setLoading(true);
    try {
      const today = new Date();
      const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      const response = await fetch(`${baseUrl}/api/leave/today-forward-leave-timetables/${pId}?date=${formattedDate}`);
      const data = await response.json();

      if (response.ok && Array.isArray(data)) {
        setLeaveTeachers(data);
        if (data.length > 0) {
            setSelectedTeacherId(data[0].employeeId); // ✅ Select first teacher
        }
      } else {
        setLeaveTeachers([]);
      }
    } catch (err) {
      console.error("Error fetching today leave teachers", err);
      setLeaveTeachers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTomorrowLeaveTeachers = async () => {
    if (!pId) return;
    setLoading(true);
    try {
      const today = new Date();
      const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      const response = await fetch(`${baseUrl}/api/leave/tomorrow-forward-leave-timetables/${pId}?date=${formattedDate}`);
      const data = await response.json();

      if (response.ok && Array.isArray(data)) {
        setLeaveTeachers(data);
         if (data.length > 0) {
            setSelectedTeacherId(data[0].employeeId); // ✅ Select first teacher
        }
      } else {
        setLeaveTeachers([]);
      }
    } catch (err) {
      console.error("Error fetching tomorrow leave teachers", err);
      setLeaveTeachers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = (event) => {
    const selectedMode = event.target.value;
    setMode(selectedMode);
    localStorage.setItem("lessonAssignMode", selectedMode);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${baseUrl}/api/lesson-assignments/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Delete failed");

      toast.success("Assignment deleted", { autoClose: 1000 });
      // You may refresh data here
    } catch (error) {
      console.error("Error deleting assignment:", error);
      toast.error("Error deleting assignment", { autoClose: 2000 });
    }
  };

  return (
    <Box>
      <Card sx={{ margin: "5% 1% 1% 1%", padding: "10px 20px 20px 20px", boxShadow: 4 }}>
        {/* Title */}
        <Typography variant="h4" component="h3" sx={{ fontWeight: "bold", mb: 2 }}>
          Leisure Period
        </Typography>

        {/* Mode Selection */}
        <RadioGroup row value={mode} onChange={handleModeChange}>
          <FormControlLabel value="auto" control={<Radio />} label="Auto Mode" />
          <FormControlLabel value="manual" control={<Radio />} label="Manual Mode" />
        </RadioGroup>

        {mode === "manual" && (
          <>
            {/* Tabs */}
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ my: 2 }}>
              <Tab label="Today Leisure Periods" />
              <Tab label="Tomorrow Leisure Periods" />
            </Tabs>

            {/* Teacher Radio + Timetable */}
            <Grid container spacing={3}>
              {/* Left Side */}
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }} >
                    Teachers on Leave
                  </Typography>
                  {loading ? (
                    <Box display="flex" justifyContent="center" mt={2}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <RadioGroup
                      name="teacher-radio"
                      value={selectedTeacherId}
                      onChange={(e) => setSelectedTeacherId(e.target.value)}
                    >
                      {leaveTeachers.map((teacher) => (
                        <FormControlLabel
                          key={teacher.employeeId}
                          value={teacher.employeeId}
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography fontWeight="bold">{teacher.teacherName}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {teacher.employeeId}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Leave Date: {teacher.leaveDate}
                              </Typography>
                            </Box>
                          }
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </RadioGroup>
                  )}
                </Card>
              </Grid>

              {/* Right Side */}
              <Grid item xs={12} md={8}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }} >
                    TimeTable
                  </Typography>

                  {loading ? (
                    <Box display="flex" justifyContent="center" mt={3}>
                      <CircularProgress />
                    </Box>
                  ) : selectedTeacherId ? (
                    (() => {
                      const selected = leaveTeachers.find((t) => t.employeeId === selectedTeacherId);
                      return selected && selected.timetable?.length > 0 ? (
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead >
                              <TableRow>
                                <TableCell  sx={{
                      borderRight: "1px solid #ddd",
                      padding: "6px",
                      textAlign: "center",
                      backgroundColor: "#fff9c4",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }} >S.No</TableCell>
                                <TableCell sx={{
                      borderRight: "1px solid #ddd",
                      padding: "6px",
                      textAlign: "center",
                      backgroundColor: "#fff9c4",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }} >Period</TableCell>
                                <TableCell sx={{
                      borderRight: "1px solid #ddd",
                      padding: "6px",
                      textAlign: "center",
                      backgroundColor: "#fff9c4",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }} >Time</TableCell>
                                <TableCell  sx={{
                      borderRight: "1px solid #ddd",
                      padding: "6px",
                      textAlign: "center",
                      backgroundColor: "#fff9c4",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }} >Class</TableCell>
                                <TableCell sx={{
                      borderRight: "1px solid #ddd",
                      padding: "6px",
                      textAlign: "center",
                      backgroundColor: "#fff9c4",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }} >Section</TableCell>
                                <TableCell  sx={{
                      borderRight: "1px solid #ddd",
                      padding: "6px",
                      textAlign: "center",
                      backgroundColor: "#fff9c4",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }} >Subject</TableCell>
                                <TableCell  sx={{
                      borderRight: "1px solid #ddd",
                      padding: "6px",
                      textAlign: "center",
                      backgroundColor: "#fff9c4",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }} >Status</TableCell>
                                <TableCell sx={{
                      borderRight: "1px solid #ddd",
                      padding: "6px",
                      textAlign: "center",
                      backgroundColor: "#fff9c4",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }}>Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selected.timetable.map((tt, index) => (
                                <TableRow key={tt.id}>
  <TableCell
    sx={{
      borderRight: "1px solid #ddd",
      padding: "4px",
      textAlign: "center",
      fontSize: "14px",
    }}
  >
    {index + 1}
  </TableCell>
  <TableCell
    sx={{
      borderRight: "1px solid #ddd",
      padding: "4px",
      textAlign: "center",
      fontSize: "14px",
    }}
  >
    {tt.period}
  </TableCell>
  <TableCell
    sx={{
      borderRight: "1px solid #ddd",
      padding: "4px",
      textAlign: "center",
      fontSize: "14px",
    }}
  >
    {tt.timeSlot}
  </TableCell>
  <TableCell
    sx={{
      borderRight: "1px solid #ddd",
      padding: "4px",
      textAlign: "center",
      fontSize: "14px",
    }}
  >
    {tt.romanNumberClass}
  </TableCell>
  <TableCell
    sx={{
      borderRight: "1px solid #ddd",
      padding: "4px",
      textAlign: "center",
      fontSize: "14px",
    }}
  >
    {tt.sections}
  </TableCell>
  <TableCell
    sx={{
      borderRight: "1px solid #ddd",
      padding: "4px",
      textAlign: "center",
      fontSize: "14px",
    }}
  >
    {tt.subject}
  </TableCell>
<TableCell
  sx={{
    borderRight: "1px solid #ddd",
    padding: "4px",
    textAlign: "center",
    fontSize: "14px",
  }}
>
  <Button
    variant="contained"
    size="small"
    sx={{
      backgroundColor:
        tt.lessorPeriodStatus === "Teacher Assgined" ? "#4CAF50" : "#FFEB3B",
      color:
        tt.lessorPeriodStatus === "Teacher Assgined" ? "#fff" : "#000",
      fontWeight: "bold",
      boxShadow: "none",
      textTransform: "none",
      "&:hover": {
        backgroundColor:
          tt.lessorPeriodStatus === "Teacher Assgined"
            ? "#388E3C"
            : "#FDD835",
      },
    }}
  >
    {tt.lessorPeriodStatus === "Teacher Assgined"
      ? "Assigned"
      : "Not Assigned"}
  </Button>
</TableCell>

  <TableCell
    sx={{
      borderRight: "1px solid #ddd",
      padding: "4px",
      textAlign: "center",
      fontSize: "14px",
    }}
  >
    <Button
      variant="contained"
      sx={{
        backgroundColor: "#1976d2",
        margin: "4px",
        fontSize: "0.6rem",
        "&:hover": {
          backgroundColor: "#1565c0",
        },
      }}
      onClick={() => {
        console.log("Navigating with row:", tt);
        navigate("/Teacherlist", {
          state: {
            rowData: tt,
            leaveDate: selected.leaveDate,
          },
        });
      }}
    >
      View
    </Button>
  </TableCell>
</TableRow>

                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography>No timetable available for selected teacher.</Typography>
                      );
                    })()
                  ) : (
                    <Typography>Select a teacher to view timetable.</Typography>
                  )}
                </Card>
              </Grid>
            </Grid>
          </>
        )}

        <ToastContainer position="top-right" />
      </Card>
    </Box>
  );
}
